import type {Key, Node, SingleSelection} from '@react-types/shared';
import type {SingleSelectListState} from '@sail/react-aria';
import {useControlledState} from '@sail/react-aria';
import {useMemo} from 'react';
import type {Collection, Item} from '../deprecated/components/collections';
import {useListState} from '../deprecated/components/collections';

/**
 * A replacement for
 * [useSingleSelectListState](https://react-spectrum.adobe.com/react-stately/useSingleSelectListState.html)
 * Provides state management for list-like components with single selection.
 * Handles building a collection of items from props, and manages selection state.
 */
export function useSingleSelectListState<T>({
  getItems,
  filter,
  disabledKeys,
  selection,
}: {
  getItems: Collection<T>['getItems'];
  filter?: (items: Array<Item<T>>) => Array<Item<T>>;
  disabledKeys?: Set<Key>;
  selection: SingleSelection;
}): SingleSelectListState<T> {
  const {
    selectedKey: value,
    defaultSelectedKey,
    onSelectionChange,
  } = selection as Required<SingleSelection>;

  const [selectedKey, setSelectedKey] = useControlledState(
    value as Key,
    defaultSelectedKey ?? null,
    onSelectionChange,
  );
  const selectedKeys = useMemo(
    () => (selectedKey != null ? [selectedKey] : []),
    [selectedKey],
  );
  const {
    collection,
    disabledKeys: disabledKeysOut,
    selectionManager,
  } = useListState({
    getItems,
    filter,
    disabledKeys,
    selection: {
      selectionMode: 'single',
      disallowEmptySelection: true,
      allowDuplicateSelectionEvents: true,
      selectedKeys,
      onSelectionChange: (keys) => {
        const key = (keys as Set<Key>).values().next().value;

        // Always fire onSelectionChange, even if the key is the same
        // as the current key (useControlledState does not).
        if (key === selectedKey && onSelectionChange) {
          onSelectionChange(key);
        }

        if (key) {
          setSelectedKey(key.toString());
        }
      },
    },
  });

  const selectedItem = collection.getItem(selectedKey) as Node<T>;

  return {
    collection,
    disabledKeys: disabledKeysOut,
    selectionManager,
    selectedKey,
    setSelectedKey,
    selectedItem,
  };
}
